"use client";

import React from "react";

import { cn } from "@/lib/utils";

/**
 * Scroll reveal wrapper. Owns *when* a section animates; the motion itself
 * lives on `.reveal-item` in animation.css. See
 * documentation/loading-animations.md for the full design.
 *
 * One IntersectionObserver per section, one-shot. No scroll listener, no
 * motion library (framer-motion is not in this codebase).
 *
 * STRUCTURAL STABILITY (performance.md §1 — the most expensive lesson in this
 * repo): a CSS animation restarts when its DOM node is recreated, and React
 * recreates a subtree when the element *type* at a position changes. So this
 * component renders the SAME element type in every state and only swaps a
 * className. The `stagger` branch below picks between two shapes, but
 * `stagger` is a static prop decided at the call site and never changes at
 * runtime, so no position ever changes type after hydration.
 */

/** How far up the viewport a section must come before it reveals: 20% of the
 *  viewport height, so it triggers where the reader is actually looking rather
 *  than at the first pixel of overlap. */
const ROOT_MARGIN = "0px 0px -20% 0px";

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export interface SectionRevealProps {
  children: React.ReactNode;
  /**
   * `false` (default): the wrapper supplies ONE implicit `.reveal-item` around
   * everything, so the section fades in as a single unit. Text, gallery,
   * accordion and work sections use this.
   *
   * `true`: the section's own markup supplies the `.reveal-item` elements with
   * their own `--reveal-index` values, so its parts stagger. Card sections use
   * this (the header, the grid cells and the carousel slides each opt in).
   */
  stagger?: boolean;
  /**
   * For the section directly below the hero, which is already on screen at
   * load and would therefore never be triggered by scrolling.
   *
   * This skips the observer entirely and hands the reveal to CSS, which runs
   * it off the hero's own `html[data-entrance]` clock. Nothing here hydrates,
   * which matters because this is the one revealed section that is above the
   * fold.
   *
   * **Only ever set this on the homepage.** A CMS page has no hero, so the
   * attribute timing it waits on describes an entrance that never plays.
   */
  afterHero?: boolean;
  className?: string;
}

export function SectionReveal({
  children,
  stagger = false,
  afterHero = false,
  className,
}: SectionRevealProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = React.useState(false);

  React.useEffect(() => {
    // The after-hero path is pure CSS; there is nothing to observe.
    if (afterHero) return;

    const el = ref.current;
    if (!el) return;

    // Both escape hatches reveal immediately rather than risk leaving content
    // hidden: reduced motion (the CSS also forces the end state), and a
    // browser with no IntersectionObserver.
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate: revealing is the safe terminal state, and both branches here mean "we cannot observe", so there is nothing to wait for
      setRevealed(true);
      return;
    }

    let observer: IntersectionObserver | null = null;
    let innerRaf = 0;

    // Double rAF before observing. On the very first paint the layout hasn't
    // settled — images are still resolving and sections that will end up far
    // below the fold can momentarily measure as in-viewport. Observing then
    // fires everything at once, invisibly, and the reader scrolls down later
    // to find every section already revealed and nothing animating. Waiting
    // two frames means we measure the real layout.
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(() => {
        observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            setRevealed(true);
            // One-shot: the reveal never replays.
            observer?.disconnect();
          },
          { rootMargin: ROOT_MARGIN, threshold: 0 },
        );
        observer.observe(el);
      });
    });

    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      observer?.disconnect();
    };
  }, [afterHero]);

  const stateClass = afterHero
    ? "reveal-after-hero"
    : revealed
      ? "section-reveal-loaded"
      : "section-reveal-loading";

  // `afterHero` always renders as a single unit, ignoring `stagger`: its
  // animation has to finish before PageLoader removes `data-entrance` at
  // ~1600ms, and a per-item stagger would push later items past that and make
  // them snap. See the timing note in animation.css.
  const singleUnit = afterHero || !stagger;

  return (
    <div ref={ref} className={cn(stateClass, className)}>
      {singleUnit ? <div className="reveal-item">{children}</div> : children}
    </div>
  );
}

export default SectionReveal;
